import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Attachment as DefaultAttachment } from 'stream-chat-react';
import convert from 'heic-convert/browser';
import chatTranslateInstance from '../../../common/services/chatTranslate';

const HEIC_TO_JPEG_QUALITY = 0.9;

const ConvertingPlaceholder = styled.div`
  width: 200px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  border-radius: 8px;
  color: #666;
  font-size: 14px;
`;

// Check if an attachment is HEIC
const isHeicAttachment = (attachment) => {
  const imageUrl = attachment?.image_url || attachment?.thumb_url;
  return imageUrl && (
    imageUrl.toLowerCase().includes('.heic')
    || imageUrl.toLowerCase().includes('.heif')
    || attachment?.mime_type === 'image/heic'
    || attachment?.mime_type === 'image/heif'
  );
};

// Convert HEIC to JPEG and return blob URL
const convertHeicToJpeg = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) throw new Error('Network response was not ok');

    const arrayBuffer = await response.arrayBuffer();
    const buffer = new Uint8Array(arrayBuffer);

    // Check if it's actually a HEIC file by looking at the magic bytes
    const isActuallyHeic = buffer.length > 12
      && String.fromCharCode(buffer[4], buffer[5], buffer[6], buffer[7]) === 'ftyp'
      && ['heic', 'mif1', 'heix'].includes(String.fromCharCode(buffer[8], buffer[9], buffer[10], buffer[11]));

    if (!isActuallyHeic) {
      // Not actually a HEIC file, use the original URL
      return imageUrl;
    }

    const outputBuffer = await convert({
      buffer,
      format: 'JPEG',
      quality: HEIC_TO_JPEG_QUALITY,
    });

    const convertedBlob = new Blob([outputBuffer], { type: 'image/jpeg' });
    return URL.createObjectURL(convertedBlob);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[CustomAttachment] HEIC conversion failed:', e);
    // Fall back to original URL on error
    return imageUrl;
  }
};

const CustomAttachment = ({ attachments, ...rest }) => {
  const i18nInstance = chatTranslateInstance.getChatTranslate();

  // Track conversion status for each attachment: { index: convertedUrl }
  const [convertedUrls, setConvertedUrls] = useState({});

  // Identify which attachments need conversion
  const heicIndices = useMemo(() => {
    if (!attachments || attachments.length === 0) return [];
    return attachments
      .map((attachment, index) => ({ attachment, index }))
      .filter(({ attachment }) => isHeicAttachment(attachment))
      .map(({ index }) => index);
  }, [attachments]);

  // Convert HEIC images
  useEffect(() => {
    if (heicIndices.length === 0) return undefined;

    let active = true;
    const localBlobUrls = [];

    const convertAll = async () => {
      try {
        const conversions = heicIndices.map(async (index) => {
          const attachment = attachments[index];
          const imageUrl = attachment?.image_url || attachment?.thumb_url;
          const convertedUrl = await convertHeicToJpeg(imageUrl);

          // Track blob URLs for cleanup
          if (convertedUrl?.startsWith('blob:')) {
            localBlobUrls.push(convertedUrl);
          }

          return { index, convertedUrl };
        });

        const results = await Promise.all(conversions);

        if (active) {
          const newConvertedUrls = results.reduce((acc, { index, convertedUrl }) => ({
            ...acc,
            [index]: convertedUrl,
          }), {});
          setConvertedUrls(newConvertedUrls);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[CustomAttachment] Error converting HEIC images:', error);
      }
    };

    convertAll();

    return () => {
      active = false;
      // Cleanup blob URLs created in this effect
      localBlobUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore cleanup errors
        }
      });
    };
  }, [heicIndices, attachments]);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  // Check if all conversions are complete
  const allConversionsComplete = heicIndices.every((index) => convertedUrls[index] != null);

  // If there are HEIC images and not all are converted yet, show placeholder
  if (heicIndices.length > 0 && !allConversionsComplete) {
    return (
      <div className="str-chat__message-attachment">
        <ConvertingPlaceholder>
          {i18nInstance.t('MESSAGE_CONVERTING_IMAGE')}
        </ConvertingPlaceholder>
      </div>
    );
  }

  // Create new attachments array with converted URLs
  const processedAttachments = attachments.map((attachment, index) => {
    if (convertedUrls[index]) {
      // This was a HEIC image, use the converted URL
      return {
        ...attachment,
        image_url: convertedUrls[index],
        thumb_url: convertedUrls[index],
        type: 'image',
      };
    }
    // Non-HEIC image, ensure it has type: 'image'
    return {
      ...attachment,
      type: attachment.type || 'image',
    };
  });

  // Pass all attachments to DefaultAttachment (it will handle gallery grouping)
  return <DefaultAttachment attachments={processedAttachments} {...rest} />;
};

CustomAttachment.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      image_url: PropTypes.string,
      thumb_url: PropTypes.string,
      mime_type: PropTypes.string,
      fallback: PropTypes.string,
      type: PropTypes.string,
    }),
  ).isRequired,
};

export default CustomAttachment;
